import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Spinner, Button, Table } from "react-bootstrap";
import {
  FiUsers,
  FiSearch,
  FiAlertTriangle,
  FiArrowLeft,
  FiUser,
  FiUserCheck,
  FiTrendingUp,
  FiTarget,
  FiUsers as FiUsersIcon,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import "./AdminReport.css";

const AdminReport = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: "",
    role: "all",
  });

  const roles = [
    { value: "all", label: "All Roles", icon: FiUsersIcon },
    { value: "director", label: "Director", icon: FiTrendingUp },
    { value: "zonal_head", label: "Zonal Head", icon: FiTarget },
    { value: "branch_manager", label: "Branch Manager", icon: FiUserCheck },
    { value: "team_lead", label: "Team Lead", icon: FiUsers },
    { value: "relationship_manager", label: "Relationship Manager", icon: FiUser },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("adminToken");

    if (!token) {
      setError("You are not logged in as an administrator. Please log in first.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/api/admin/admin-report", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(response.data);
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setError("Your session has expired. Please log in again.");
          // Optionally clear the token
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminRole");
          localStorage.removeItem("adminName");
        } else if (err.response.status === 403) {
          setError("You don't have permission to access this report.");
        } else {
          setError(`Server error: ${err.response.data.message || 'Unknown error'}`);
        }
      } else if (err.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Could not load user data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = users;

    // Filter by search term
    if (filters.searchTerm) {
      filtered = filtered.filter(
        (user) =>
          (user.name?.toLowerCase() || "").includes(
            filters.searchTerm.toLowerCase()
          ) ||
          (user.username?.toLowerCase() || "").includes(
            filters.searchTerm.toLowerCase()
          )
      );
    }

    // Filter by role
    if (filters.role !== "all") {
      // Convert filter role back to underscore format for comparison
      const filterRoleUnderscore = filters.role;
      filtered = filtered.filter((user) => {
        // Convert user role back to underscore format for comparison
        const userRoleUnderscore = user.role.replace(/\s+/g, '_').toLowerCase();
        return userRoleUnderscore === filterRoleUnderscore;
      });
    }

    setFilteredUsers(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="dashboard-container">
      <aside className="navigator-panel">
        <header className="navigator-header">
          <FiUsers /> Admin Report
        </header>
        <ul className="role-filter-list">
          {roles.map((role) => {
            const IconComponent = role.icon;
            return (
              <li key={role.value} className="role-filter-item">
                <button
                  className={filters.role === role.value ? "active" : ""}
                  onClick={() => handleFilterChange("role", role.value)}
                >
                  <IconComponent className="me-2" size={16} />
                  {role.label}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <main className="content-panel">
        <div className="content-header d-flex justify-content-between align-items-center mb-4">
          <h2 className="page-title mb-0">
            <FiUsers className="me-2" />
            Admin Report - User Activity
          </h2>
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/admin-dashboard")}
            >
              <FiArrowLeft className="me-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>

        <div className="filter-bar">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FiSearch className="me-2" /> Search Users
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by Name or Username..."
                  value={filters.searchTerm}
                  onChange={(e) =>
                    handleFilterChange("searchTerm", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={filters.role}
                  onChange={(e) =>
                    handleFilterChange("role", e.target.value)
                  }
                  className="form-select-with-icons"
                >
                  {roles.map((role) => {
                    const IconComponent = role.icon;
                    return (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {loading && (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        )}

        {error && (
          <div className="text-center p-5 text-danger">
            <FiAlertTriangle className="mb-2" size={24} />
            <p>{error}</p>
            <Button variant="primary" size="sm" onClick={fetchUsers}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && (
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-dark">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Location</th>
                  <th>Login Count</th>
                  <th>Password Change Count</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      No users found matching the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.username}</td>
                  <td>{user.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                      <td>{user.location || 'N/A'}</td>
                      <td>{user.login_count}</td>
                      <td>{user.password_change_count}</td>
                      <td>{user.last_login}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminReport;
